import type { GmCapability } from "./capabilities.ts";

/**
 * Feedback from the game as shared data (ADR-081's amendment of 2026-10-05): the kinds of report, which of them a
 * set of capabilities may send, who the server records as the reporter, the states an account's access can be in
 * and the ladder a pause climbs. The server decides everything from an account's live roles; a page reads these
 * only to decide what to show.
 */

/**
 * The kinds of report, a bug report first: it is the one every account may send.
 */
export const FEEDBACK_KINDS = ["bug", "feature"] as const;

/**
 * One kind of report.
 */
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/**
 * The capability a feature request needs, which every staff role holds and no player does.
 */
export const FEEDBACK_CAPABILITY: GmCapability = "requests.send.game";

/**
 * Who sent a report, as the server records it from the account's live roles.
 */
export type FeedbackReporter = "player" | "staff";

/**
 * The states an account's access to feedback can be in: open, paused until a moment, or turned off by a GM.
 */
export const FEEDBACK_ACCESS_STATES = ["open", "paused", "off"] as const;

/**
 * One state of access.
 */
export type FeedbackAccessState = (typeof FEEDBACK_ACCESS_STATES)[number];

/**
 * How long each pause the limits give lasts, in minutes: an hour, a day, then seven days for the third and every
 * one after.
 */
export const FEEDBACK_PAUSE_LADDER_MINUTES = [60, 1440, 10_080] as const;

/**
 * The longest pause a GM may set, in minutes: 30 days.
 */
export const FEEDBACK_PAUSE_MAX_MINUTES = 43_200;

/**
 * Who a set of capabilities makes its holder when it sends a report.
 *
 * @param capabilities What the account's live roles grant.
 * @returns `staff` with the game's feedback capability, `player` without it.
 */
export function feedbackReporterOf(capabilities: readonly GmCapability[]): FeedbackReporter {
  return capabilities.includes(FEEDBACK_CAPABILITY) ? "staff" : "player";
}

/**
 * The kinds of report a set of capabilities may send.
 *
 * @param capabilities What the account's live roles grant; empty for a player.
 * @returns A bug report for every account, and a feature request with the game's feedback capability.
 * @example
 * feedbackKindsOf([]);
 * // ["bug"]
 */
export function feedbackKindsOf(capabilities: readonly GmCapability[]): readonly FeedbackKind[] {
  return feedbackReporterOf(capabilities) === "staff" ? FEEDBACK_KINDS : ["bug"];
}

/**
 * How long a pause lasts at a step of the ladder.
 *
 * @param step The step, 1 for an account's first pause; anything below is the first.
 * @returns The minutes: the ladder's entry, and its last for every step past it.
 */
export function feedbackPauseMinutes(step: number): number {
  const index = Math.min(Math.max(Math.trunc(step), 1), FEEDBACK_PAUSE_LADDER_MINUTES.length) - 1;
  return FEEDBACK_PAUSE_LADDER_MINUTES[index] as number;
}
